// Maps kept in this browser (design §14.8): the data of planes opened before,
// so that opening one again fetches only what changed since. Kept per world,
// plane, view and account; signing out removes them all. Browser storage may
// be missing or refuse (a private window, cleared site data): then nothing is
// kept, and planes are read whole as before.

const DB = 'atlas-maps';
const STORE = 'planes';

let opening: Promise<IDBDatabase | null> | null = null;

function db(): Promise<IDBDatabase | null> {
  return (opening ??= new Promise((done) => {
    try {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(STORE);
      r.onsuccess = () => done(r.result);
      r.onerror = () => done(null);
      r.onblocked = () => done(null);
    } catch {
      done(null);
    }
  }));
}

/** What was kept under a key, or null. */
export async function readMap<T>(key: string): Promise<T | null> {
  const d = await db();
  if (!d) return null;
  return new Promise((done) => {
    try {
      const r = d.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      r.onsuccess = () => done((r.result as T | undefined) ?? null);
      r.onerror = () => done(null);
    } catch {
      done(null);
    }
  });
}

/**
 * Keeps what `make` gives under a key. It is called once storage is ready, and
 * the browser copies its value at once: a map and the versions worked out from
 * it are kept as they were together, whatever changes after.
 */
export async function keepMap(key: string, make: () => unknown): Promise<void> {
  const d = await db();
  if (!d) return;
  try {
    d.transaction(STORE, 'readwrite').objectStore(STORE).put(make(), key);
  } catch {
    /* full, or refused: the next opening reads the plane whole */
  }
}

/** Removes every kept map (when signing out). */
export async function clearMaps(): Promise<void> {
  const d = await db();
  d?.close();
  opening = null;
  await new Promise<void>((done) => {
    try {
      const r = indexedDB.deleteDatabase(DB);
      r.onsuccess = r.onerror = r.onblocked = () => done();
    } catch {
      done();
    }
  });
}
